import { makeStyles } from '@material-ui/core/styles';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { getFromLS, isAdmin } from '@orientaction/utils';
import { ReactNode, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useSnackbar } from 'react-simple-snackbar';
import { FeedbackModal } from '../../pages/Redesign/shared/feedback';
import useChatbotFeedbackSubmit from '../../hooks/useChatbotFeedbackSubmit';
import useIAChat from '../../hooks/useIAChat';
import { IAChatFloatingButton, IAChatPanel } from '../../pages/Redesign/shared/iachat';
import { checkChatbotReviewExists } from '../../services/ai.service';

const useStyles = makeStyles({
  layoutContainer: {
    position: 'relative',
    minHeight: '100vh',
  },
  '@global': {
    '[class*="Snackbar_snackbar-wrapper"]': {
      zIndex: '1200 !important',
    },
  },
});

interface LayoutBeneficiaryProps {
  children: ReactNode;
}

const LayoutBeneficiary = ({ children }: LayoutBeneficiaryProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMinorResultRoute = Boolean(
    useRouteMatch({ path: '/mes-resultats/:id/:idUser?', exact: true })
  );
  const user = useMemo(() => {
    try {
      return JSON.parse(getFromLS('userInfos') || '{}');
    } catch {
      return {};
    }
  }, []);
  const showIAChat = !isMinorResultRoute || !isAdmin(user);
  const [openSnackbar] = useSnackbar({
    position: isMobile ? 'top-center' : 'bottom-right',
    style: {
      position: 'fixed',
      backgroundColor: '#0E1247',
      color: '#FFFFFF',
      fontFamily: 'Poppins, sans-serif',
      fontSize: '16px',
      fontWeight: 400,
      borderRadius: '8px',
      padding: '8px 8px',
      minWidth: '400px',
      minHeight: '48px',
      boxShadow: '0px 4px 12px rgba(14, 18, 71, 0.3)',
      display: 'flex',
      alignItems: 'center',
      ...(isMobile
        ? {
            top: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            right: 'auto',
            bottom: 'auto',
          }
        : {
            bottom: '24px',
            right: '24px',
            left: 'auto',
            top: 'auto',
            transform: 'none',
          }),
      margin: 0,
    },
  });

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackChatbotId, setFeedbackChatbotId] = useState<number | null>(null);

  const handleChatbotClose = async (chatbotId: number) => {
    try {
      const hasReview = await checkChatbotReviewExists(chatbotId);

      if (!hasReview) {
        setFeedbackChatbotId(chatbotId);
        setTimeout(() => {
          setShowFeedbackModal(true);
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking for existing review:', error);
    }
  };

  const {
    isDrawerOpen,
    openDrawer,
    minimizeDrawer,
    closeDrawer,
    messages,
    inputValue,
    setInputValue,
    sendMessage,
    selectPredefinedQuery,
    isLoading,
    viewMode,
    chatbotHistory,
    isLoadingHistory,
    showHistory,
    backToMain,
    loadConversation,
  } = useIAChat({ onCloseCallback: handleChatbotClose });

  const { submitFeedback, isSubmitting, error, reset } =
    useChatbotFeedbackSubmit(feedbackChatbotId);

  const handleSendMessage = () => {
    sendMessage(inputValue);
  };

  const handleFeedbackClose = () => {
    setShowFeedbackModal(false);
    setFeedbackChatbotId(null);
    reset();
  };

  const handleFeedbackSubmit = async (rating: number, comment: string) => {
    const success = await submitFeedback(rating, comment);
    if (success) {
      setShowFeedbackModal(false);
      setFeedbackChatbotId(null);
      openSnackbar(
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="18" fill="#23296F" />
            <path
              d="M24 14L16 22L12 18"
              stroke="#FFFFFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Votre avis a bien été pris en compte</span>
        </div>
      );
    }
  };

  return (
    <div className={classes.layoutContainer}>
      {children}
      {showIAChat && (
        <>
          <IAChatFloatingButton onClick={openDrawer} />
          <IAChatPanel
            open={isDrawerOpen}
            onMinimize={minimizeDrawer}
            onClose={closeDrawer}
            messages={messages}
            inputValue={inputValue}
            onInputChange={setInputValue}
            onSendMessage={handleSendMessage}
            onSelectPredefinedQuery={selectPredefinedQuery}
            isLoading={isLoading}
            viewMode={viewMode}
            chatbotHistory={chatbotHistory}
            isLoadingHistory={isLoadingHistory}
            onShowHistory={showHistory}
            onBackToMain={backToMain}
            onLoadConversation={loadConversation}
          />
          <FeedbackModal
            open={showFeedbackModal}
            onClose={handleFeedbackClose}
            onSubmit={handleFeedbackSubmit}
            title="Qu'avez vous pensé de l'échange ?"
            isSubmitting={isSubmitting}
            error={error || undefined}
          />
        </>
      )}
    </div>
  );
};

export default LayoutBeneficiary;
